import axios from 'axios';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Typography, TextField, Button, Box, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import logo from '../components/images/sodtf logo dark stacked.png';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { auth } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';

const textFieldSx = {
  backgroundColor: 'white',
  borderRadius: 0,
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
};

const buttonSx = {
  borderColor: 'rgb(167, 198, 103)',
  borderWidth: '2.5px',
  color: 'rgb(167, 198, 103)',
  borderRadius: '15px',
  textTransform: 'none',
  marginTop: 2,
  width: '13rem',
  alignSelf: 'center',
  fontWeight: '500',
  fontSize: '1.1rem',
  py: 0.75,
  '&:hover': {
    borderColor: 'rgb(157, 201, 70))',
    color: 'rgb(157, 201, 70)',
    backgroundColor: 'rgba(157, 201, 70, 0.57))',
  },
};

const normalizePhoneNumber = (value = '') => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('+') && trimmed.length > 8) {
    return trimmed;
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return null;
};

function Signup() {
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('form');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [createdEmail, setCreatedEmail] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = new URLSearchParams(location.search).get('returnUrl');

  const formatSignupError = (err) => {
    if (!err) return 'Unable to create your account right now.';
    if (err.response?.data?.message) return err.response.data.message;
    if (err.message) return err.message;
    return 'Unable to create your account right now.';
  };

  const autoLoginAfterSignup = async (email, password) => {
    let userCredential;
    try {
      userCredential = await signInWithEmailAndPassword(auth, email, password);
    } catch (authErr) {
      console.error('Auto-login after signup failed:', authErr);
      throw new Error('Account created, but we could not sign you in automatically. Please try logging in to continue.');
    }

    const user = userCredential.user;
    try {
      await user.getIdToken(true);
      const token = await user.getIdToken();
      localStorage.setItem('token', token);
    } catch (tokenErr) {
      console.warn('Failed to persist auth token after signup:', tokenErr);
    }

  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (stage !== 'form') return;
    setError('');
    setSuccessMessage('');
    setLoading(true);
    let accountCreated = false;
    try {
      const normalizedPhone = normalizePhoneNumber(form.phone);
      if (!normalizedPhone) {
        throw new Error('Enter a valid phone number with area code.');
      }
      const response = await axios.post(process.env.REACT_APP_CREATE_ACCOUNT_URL, {
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: normalizedPhone,
        skipVerificationEmail: true,
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Account creation failed.');
      }

      accountCreated = true;
      await autoLoginAfterSignup(form.email, form.password);

      setCreatedEmail(form.email);
      setStage('complete');
      setSuccessMessage(
        `You're signed in! Your account is ready to use. Click Continue to start shopping.`
      );
    } catch (err) {
      if (accountCreated) {
        setError(err?.message || 'Your account was created, but we could not finish signing you in. Please try logging in to continue.');
      } else {
        setError(formatSignupError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const continueAfterSignup = () => {
    const destination = returnUrl || '/profile';
    navigate(destination);
  };

  const headingText = stage === 'complete' ? 'Account Created' : 'Create an Account';

  const renderAccountForm = (isMobileLayout) => (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: isMobileLayout ? '80%' : '95%',
        gap: 1.5,
        mt: isMobileLayout ? 2 : 0,
      }}
    >
      {error && (
        <Typography color="error" align="center" sx={{ width: '100%' }}>
          {error}
        </Typography>
      )}
      <TextField
        name="firstName"
        label="First Name"
        variant="outlined"
        required
        fullWidth
        sx={textFieldSx}
        onChange={handleChange}
      />
      <TextField
        name="lastName"
        label="Last Name"
        variant="outlined"
        required
        fullWidth
        sx={textFieldSx}
        onChange={handleChange}
      />
      <TextField
        name="phone"
        label="Phone Number"
        variant="outlined"
        required
        fullWidth
        type="tel"

        sx={textFieldSx}
        onChange={handleChange}
      />
      <TextField
        type="email"
        name="email"
        label="Email"
        variant="outlined"
        helperText="Used for two-factor verification codes"
        required
        fullWidth
        sx={textFieldSx}
        onChange={handleChange}
      />
      <TextField
        type={showPassword ? 'text' : 'password'}
        name="password"
        label="Password"
        variant="outlined"
        required
        fullWidth
        sx={textFieldSx}
        onChange={handleChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button type="submit" variant="outlined" disabled={loading} sx={buttonSx}>
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign Up'}
      </Button>
    </Box>
  );

  const renderCompletionView = (isMobileLayout) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: isMobileLayout ? '80%' : '95%',
        gap: 2,
        textAlign: 'center',
        mt: isMobileLayout ? 2 : 0,
      }}
    >
      <Typography sx={{ color: '#5A6872' }}>
        {successMessage ||
          `You're signed in and your account is ready to use. Click Continue to start shopping.`}
      </Typography>
      <Button type="button" variant="outlined" onClick={continueAfterSignup} sx={buttonSx}>
        Continue
      </Button>
    </Box>
  );

  const renderStageContent = (isMobileLayout) => {
    if (stage === 'complete') {
      return renderCompletionView(isMobileLayout);
    }
    return renderAccountForm(isMobileLayout);
  };

  return (
    <Container
      maxWidth="lg"
      disableGutters
      sx={{
        display: 'flex',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        alignSelf: 'center',
        padding: 5,
        width: '100vw',
        boxSizing: 'border-box',
      }}
    >
      {isMobile ? (
        <div
          id="container"
          style={{
            borderRadius: 20,
            backgroundColor: '#ffffffff',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '70vh',
            width: '80vw',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 5,
            boxShadow: '0 4px 8px rgba(179, 179, 179, 0.5)'
          }}
        >
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent', height: '100%', width: '100%' }}>
            <img src={logo} alt="See Our Designs" style={{ maxWidth: '60%', maxHeight: '60%' }} />
          </div>
          <div
            style={{
              flex: 1,
              backgroundColor: '#eaf3d4',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
              fontFamily: 'Anek Latin'
            }}
          >
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              sx={{
                fontFamily: 'Anek Latin',
                mb: 0,
                mt: 2,
                fontWeight: 'bold',
                color: '#5A6872',
                textAlign: 'center',
                width: '100%',
              }}
            >
              {headingText}
            </Typography>
            {renderStageContent(true)}
          </div>
        </div>
      ) : (
        <Box
          sx={{
            borderRadius: 5,
            backgroundColor: 'white',
            display: 'flex',
            height: '70vh',
            width: '80vw',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent' }}>
            <img src={logo} alt="See Our Designs" style={{ maxWidth: '60%', maxHeight: '60%' }} />
          </Box>
          <Box
            sx={{
              flex: 1,
              bgcolor: '#eaf3d4',
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              borderTopRightRadius: 20,
              borderBottomRightRadius: 20,
              height: '100%',
              boxSizing: 'border-box',
              minWidth: 0,
              minHeight: 0,
              fontFamily: 'Anek Latin'
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontFamily: 'Anek Latin', mb: 2, fontWeight: 'bold', color: '#5A6872', textAlign: 'center', width: '100%' }}
            >
              {headingText}
            </Typography>
            {renderStageContent(false)}
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default Signup;
